import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Map, ArrowRight } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-id="element-86">
      {/* Background Texture & Topography */}
      <div className="absolute inset-0 bg-paper-texture opacity-50 pointer-events-none" data-id="element-87" />

      {/* Animated Topographic Lines (SVG) */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" data-id="element-88">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-89">
          <motion.path d="M0,50 Q25,30 50,50 T100,50" fill="none" stroke="#1a3a2a" strokeWidth="0.2" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          ease: 'easeInOut'
        }} data-id="element-90" />
          <motion.path d="M0,60 Q25,40 50,60 T100,60" fill="none" stroke="#1a3a2a" strokeWidth="0.2" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          delay: 0.5,
          ease: 'easeInOut'
        }} data-id="element-91" />
          <motion.path d="M0,40 Q25,20 50,40 T100,40" fill="none" stroke="#1a3a2a" strokeWidth="0.2" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          delay: 1,
          ease: 'easeInOut'
        }} data-id="element-92" />
          {/* Concentric irregular circles for topo effect */}
          <motion.circle cx="80" cy="20" r="15" fill="none" stroke="#C4572A" strokeWidth="0.1" initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 0.2
        }} transition={{
          duration: 4,
          repeat: Infinity,
          repeatType: 'reverse'
        }} data-id="element-93" />
          <motion.circle cx="80" cy="20" r="10" fill="none" stroke="#C4572A" strokeWidth="0.1" initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 0.3
        }} transition={{
          duration: 4,
          delay: 0.5,
          repeat: Infinity,
          repeatType: 'reverse'
        }} data-id="element-94" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid md:grid-cols-2 gap-12 items-center" data-id="element-95">
        {/* Text Content */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="text-center md:text-left" data-id="element-96">
          <div className="inline-flex items-center space-x-2 bg-brown/10 px-3 py-1 rounded-full mb-6 border border-brown/20" data-id="element-97">
            <Compass className="w-4 h-4 text-terracotta" data-id="element-98" />
            <span className="text-sm font-bold text-brown uppercase tracking-wider" data-id="element-99">
              Cartography for Travelers
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-forest leading-tight mb-6" data-id="element-100">
            Chart Your <br data-id="element-101" />
            <span className="text-terracotta italic relative" data-id="element-102">
              Own Course
              <svg className="absolute -bottom-2 left-0 w-full h-3 text-terracotta/30" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-103">
                <path d="M0,5 Q50,10 100,5" stroke="currentColor" strokeWidth="2" fill="none" data-id="element-104" />
              </svg>
            </span>
          </h1>

          <p className="text-lg md:text-xl text-forest/80 mb-8 max-w-lg mx-auto md:mx-0 leading-relaxed font-sans" data-id="element-105">
            Maplekeep turns scattered travel plans into beautifully mapped
            journeys. Collect pins, keep field notes, and explore terrain data —
            all in one cartographer's toolkit.
          </p>

          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-4 justify-center md:justify-start" data-id="element-106">
            <button className="w-full sm:w-auto bg-terracotta text-parchment px-8 py-4 rounded-sm font-serif font-bold text-lg hover:bg-terracotta-hover transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center group" data-id="element-107">
              Start Your Expedition
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-108" />
            </button>
            <button className="w-full sm:w-auto px-8 py-4 rounded-sm font-serif font-bold text-lg text-forest border-2 border-forest/20 hover:bg-forest/5 transition-all duration-300 flex items-center justify-center" data-id="element-109">
              <Map className="mr-2 w-5 h-5" data-id="element-110" />
              View the Atlas
            </button>
          </div>
        </motion.div>

        {/* Visual Element - Map Illustration */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1,
        delay: 0.2
      }} className="relative hidden md:block" data-id="element-111">
          <div className="relative w-full aspect-square max-w-md mx-auto" data-id="element-112">
            {/* Decorative Map Layers */}
            <div className="absolute inset-0 bg-parchment-dark rounded-lg transform rotate-3 border border-brown/20 shadow-xl" data-id="element-113" />
            <div className="absolute inset-0 bg-parchment rounded-lg transform -rotate-2 border border-brown/20 shadow-lg flex items-center justify-center overflow-hidden" data-id="element-114">
              {/* Abstract Map Content */}
              <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-sage/30 to-transparent" data-id="element-115" />

              {/* Contour Lines */}
              <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 200 200" data-id="element-116">
                <path d="M20,100 Q60,50 100,100 T180,100" fill="none" stroke="#1a3a2a" strokeWidth="1" data-id="element-117" />
                <path d="M20,120 Q60,70 100,120 T180,120" fill="none" stroke="#1a3a2a" strokeWidth="1" data-id="element-118" />
                <path d="M20,140 Q60,90 100,140 T180,140" fill="none" stroke="#1a3a2a" strokeWidth="1" data-id="element-119" />
                <path d="M20,80 Q60,30 100,80 T180,80" fill="none" stroke="#1a3a2a" strokeWidth="1" data-id="element-120" />
              </svg>

              {/* Pins */}
              <motion.div className="absolute top-1/3 left-1/3" animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-121">
                <div className="relative" data-id="element-122">
                  <div className="w-8 h-8 bg-terracotta rounded-full flex items-center justify-center shadow-lg border-2 border-parchment z-10 relative" data-id="element-123">
                    <Map className="w-4 h-4 text-parchment" data-id="element-124" />
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-4 bg-gray-400" data-id="element-125" />
                  <div className="absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 w-3 h-1 bg-black/20 rounded-full blur-[1px]" data-id="element-126" />
                </div>
              </motion.div>

              <motion.div className="absolute bottom-1/3 right-1/3" animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.5
            }} data-id="element-127">
                <div className="relative" data-id="element-128">
                  <div className="w-8 h-8 bg-forest rounded-full flex items-center justify-center shadow-lg border-2 border-parchment z-10 relative" data-id="element-129">
                    <Compass className="w-4 h-4 text-parchment" data-id="element-130" />
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0.5 h-4 bg-gray-400" data-id="element-131" />
                  <div className="absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 w-3 h-1 bg-black/20 rounded-full blur-[1px]" data-id="element-132" />
                </div>
              </motion.div>
            </div>

            {/* Compass Rose Decoration */}
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-parchment rounded-full border-4 border-gold shadow-lg flex items-center justify-center p-2 z-20" data-id="element-133">
              <Compass className="w-full h-full text-navy animate-spin-slow" style={{
              animationDuration: '10s'
            }} data-id="element-134" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}